import type { Attribution } from '../services/pitruLeadService';

const KEY = 'pp_v2_attribution';
let memory: Attribution | undefined;
const cleanURL = (value: string) => {
  try { const url = new URL(value); return /^https?:$/.test(url.protocol) ? `${url.origin}${url.pathname}`.slice(0, 1000) : undefined; }
  catch { return undefined; }
};

/** Capture a V2 entry, independent of GA. A tagged landing visit starts a new campaign context.
 * Navigation to consultation preserves it. URLs omit queries/fragments to avoid storing PII. */
export function capturePitruAttribution(): Attribution {
  const path = window.location.pathname.replace(/\/$/, '');
  if (path !== '/pitru-paksha' && !path.startsWith('/pitru-paksha/')) return {};
  const params = new URLSearchParams(window.location.search);
  const taggedEntry = path === '/pitru-paksha' &&
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].some(key => !!params.get(key)?.trim());
  const validEntry = (value?: string) => {
    try { return !!value && new URL(value).origin === window.location.origin &&
      /^\/pitru-paksha(?:\/|$)/.test(new URL(value).pathname); }
    catch { return false; }
  };
  if (!taggedEntry && memory && validEntry(memory.landingPage)) return memory;
  try {
    const saved = sessionStorage.getItem(KEY);
    if (saved && !taggedEntry) {
      const parsed: unknown = JSON.parse(saved);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const data = parsed as Record<string, unknown>;
        const text = (key: string) => typeof data[key] === 'string' ? (data[key] as string).slice(0, 200) : undefined;
        memory = { source: text('source'), medium: text('medium'), campaignName: text('campaignName'),
          content: text('content'), term: text('term'),
          landingPage: typeof data.landingPage === 'string' ? cleanURL(data.landingPage) : undefined,
          referralUrl: typeof data.referralUrl === 'string' ? cleanURL(data.referralUrl) : undefined };
        if (validEntry(memory.landingPage)) return memory;
      }
    }
  } catch { /* Storage unavailable or corrupted: preserve in memory. */ }
  const read = (key: string) => {
    const value = params.get(key)?.trim();
    return value && !['none', 'direct'].includes(value) ? value.slice(0, 200) : undefined;
  };
  memory = {
    source: read('utm_source'), medium: read('utm_medium'), campaignName: read('utm_campaign'),
    content: read('utm_content'), term: read('utm_term'),
    landingPage: cleanURL(window.location.href), referralUrl: cleanURL(document.referrer)
  };
  try { sessionStorage.setItem(KEY, JSON.stringify(memory)); } catch { /* memory fallback */ }
  return memory;
}
